// functions/lib/ugc/pro.ts
//
// Pro offert aux membres du programme (migration 0019) : le membre donne l'adresse e-mail de son
// compte dans l'app Basalt Lab (celle de sa connexion à l'app, que RevenueCat retrouve par son
// identifiant Supabase), l'équipe accorde le Pro à la main dans RevenueCat puis note sa date de
// fin, ou classe la demande (compte introuvable…). État lu par le portail, l'admin, le cockpit et
// l'e-mail du matin.

import { parisDate } from '../report/dates'
import type { MemberStatus } from '../../../src/lib/ugcTypes'

/** Ce que le membre et l'équipe savent du Pro. `proUntil` : dernier jour, AAAA-MM-JJ à Paris. */
export type ProFields = {
  appAccountEmail: string | null
  proRequestedAt: string | null
  proUntil: string | null
  proDismissedAt: string | null
}

export type ProState =
  | { kind: 'missing' }
  | { kind: 'requested'; requestedAt: string }
  | { kind: 'active'; until: string }
  | { kind: 'ended'; until: string }
  | { kind: 'dismissed' }
  | { kind: 'idle' }

/**
 * Demande en attente : adresse présente, demande notée, aucun Pro accordé depuis la demande
 * (`pro_until` nul ou antérieur au jour de la demande) et pas classée depuis.
 */
export function isPendingProRequest(m: ProFields): boolean {
  if (m.appAccountEmail === null || m.proRequestedAt === null) return false
  if (m.proUntil !== null && m.proUntil >= parisDate(new Date(m.proRequestedAt))) return false
  return m.proDismissedAt === null || m.proDismissedAt < m.proRequestedAt
}

/**
 * État du Pro un jour donné (`today`, AAAA-MM-JJ à Paris) : actif jusqu'à son dernier jour compris ;
 * sinon demande en attente ; sinon demande classée ; sinon Pro terminé ; sinon rien en cours.
 */
export function proState(m: ProFields, today: string): ProState {
  if (m.appAccountEmail === null) return { kind: 'missing' }
  if (m.proUntil !== null && m.proUntil >= today) return { kind: 'active', until: m.proUntil }
  if (isPendingProRequest(m) && m.proRequestedAt !== null) return { kind: 'requested', requestedAt: m.proRequestedAt }
  if (m.proDismissedAt !== null && m.proRequestedAt !== null && m.proDismissedAt >= m.proRequestedAt) return { kind: 'dismissed' }
  if (m.proUntil !== null) return { kind: 'ended', until: m.proUntil }
  return { kind: 'idle' }
}

/** Une demande de Pro que l'équipe doit traiter : membre actif ou en pause, demande en attente. */
export function waitsForPro(m: ProFields & { status: MemberStatus }): boolean {
  return (m.status === 'active' || m.status === 'paused') && isPendingProRequest(m)
}

/** Pro à retirer dans RevenueCat : membre parti dont le Pro court encore. */
export function proToRemove(m: ProFields & { status: MemberStatus }, today: string): boolean {
  return m.status === 'left' && m.proUntil !== null && m.proUntil >= today
}
